import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { take } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { LogMiddlewareServiceAttributes } from '@albatrosdeveloper/ave-models-package';
import {
  SmartGridComponent,
  PageChangedEvent,
  SortChangedEvent,
} from 'app/shared/components/smart-grid/public-api';
import { FilterChangedEvent } from 'app/shared/components/smart-grid/core/interfaces/filter-state.interface';
import {
  ResponsiveModalHeaderComponent,
  ResponsiveModalContentComponent,
} from 'app/shared/components/responsive-modal';
import { ResponsiveDialogService } from 'app/shared/services/responsive-dialog.service';
import { ResponseLatencyModalComponent } from '../../system-latency/response-latency-modal/response-latency-modal.component';
import { IntegrationErrorsPageService } from './integration-errors-page.service';
import { IntegrationErrorsCompanyLookupService } from './integration-errors-company-lookup.service';
import { IntegrationErrorDetailModalComponent } from './integration-error-detail-modal.component';
import { SpanishPaginatorIntl } from './spanish-paginator-intl';
import {
  buildIntegrationErrorsColumnDefs,
  INTEGRATION_ERRORS_GRID_CONFIG,
} from './integration-errors-modal.constants';

/**
 * Modal del badge de errores de integración SAP (header). Lista los errores
 * del día actual de `log_middleware_service` (status = ERROR) con búsqueda,
 * orden, paginación y filtros por columna server-side. Renderizado espejo de
 * la pantalla de Latencia: Interfaz (código+nombre), Tipo con iconografía
 * ERP/APP, Compañía, fechas dd-MM-yyyy HH:mm:ss, botón de respuesta, icono de
 * estado, duración y menú de acciones ("Visualizar logs" abre el detalle — no
 * hay clic en fila).
 */
@Component({
  selector: 'app-integration-errors-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    SmartGridComponent,
    ResponsiveModalHeaderComponent,
    ResponsiveModalContentComponent,
  ],
  providers: [
    IntegrationErrorsPageService,
    { provide: MatPaginatorIntl, useClass: SpanishPaginatorIntl },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-responsive-modal-header
      title="Errores de integración SAP"
      [subtitle]="subtitle()"
      icon="sync_problem"
      iconColor="red"
    />

    <app-responsive-modal-content padding="p-3" [scrollable]="false">
      <div class="flex flex-1 min-h-0 flex-col gap-3">
        <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
          <mat-icon matPrefix class="text-gray-400">search</mat-icon>
          <input
            matInput
            #searchInput
            type="text"
            placeholder="Buscar por código o proceso"
            (input)="pageService.setSearch$.next(searchInput.value)"
          />
        </mat-form-field>

        <app-smart-grid
          class="flex-1 min-h-0"
          [data]="pageService.data()"
          [columnDefs]="columnDefs"
          [config]="gridConfig()"
          [totalRecords]="pageService.totalCount()"
          [loading]="pageService.isLoading()"
          (pageChanged)="onPageChanged($event)"
          (sortChanged)="onSortChanged($event)"
          (filterChanged)="onFilterChanged($event)"
        />
      </div>
    </app-responsive-modal-content>
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
      }

      /* Cadena de altura completa: host → smart-grid → wrapper → container,
         para que la grilla llene el modal y el paginador (mt-auto) quede
         siempre al pie sin espacio muerto debajo. */
      :host ::ng-deep app-smart-grid {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
      }

      :host ::ng-deep app-smart-grid .smart-grid-wrapper {
        flex: 1;
        min-height: 0;
      }

      :host ::ng-deep app-smart-grid .smart-grid-container {
        flex: 1;
        min-height: 0;
      }
    `,
  ],
})
export class IntegrationErrorsModalComponent implements OnInit {
  readonly pageService = inject(IntegrationErrorsPageService);
  private readonly responsiveDialog = inject(ResponsiveDialogService);
  private readonly dialog = inject(MatDialog);
  private readonly companyLookup = inject(IntegrationErrorsCompanyLookupService);

  readonly columnDefs = buildIntegrationErrorsColumnDefs({
    onOpenResult: (row) => this.openResultModal(row),
    onViewLogs: (row) => this.openDetailModal(row),
  });

  readonly subtitle = computed(() => {
    const total = this.pageService.totalCount();
    return total === 1 ? '1 error hoy' : `${total} errores hoy`;
  });

  readonly gridConfig = computed(() => {
    const sort = this.pageService.sortState();
    return {
      ...INTEGRATION_ERRORS_GRID_CONFIG,
      sorting: {
        ...INTEGRATION_ERRORS_GRID_CONFIG.sorting,
        defaultSort:
          sort.column && sort.direction
            ? [
                {
                  field: sort.column,
                  direction: sort.direction.toLowerCase() as 'asc' | 'desc',
                },
              ]
            : undefined,
      },
      // El empty-state distingue "sin errores" de "no se pudo consultar": con
      // un fallo del backend la grilla vacía no debe leerse como conteo cero.
      emptyState: {
        ...INTEGRATION_ERRORS_GRID_CONFIG.emptyState,
        message: this.pageService.error()
          ? 'No se pudieron cargar los errores de integración. Intente nuevamente.'
          : INTEGRATION_ERRORS_GRID_CONFIG.emptyState.message,
      },
    };
  });

  ngOnInit(): void {
    // El catálogo de compañías se espera ANTES del primer fetch para que las
    // celdas de Compañía no rendericen sin nombres (ante error igual completa
    // y las celdas caen al fallback).
    this.companyLookup
      .ensureLoaded()
      .pipe(take(1))
      .subscribe(() => this.pageService.init());
  }

  onPageChanged(event: PageChangedEvent): void {
    this.pageService.setPagination$.next({
      pageNum: event.pageIndex + 1,
      pageSize: event.pageSize,
    });
  }

  onSortChanged(event: SortChangedEvent<LogMiddlewareServiceAttributes>): void {
    if (event.sortStates.length > 0) {
      const s = event.sortStates[0];
      this.pageService.setSort$.next({
        column: s.field as string,
        direction: s.direction.toUpperCase() as 'ASC' | 'DESC',
      });
    } else {
      this.pageService.setSort$.next({ column: null, direction: null });
    }
  }

  onFilterChanged(event: FilterChangedEvent<LogMiddlewareServiceAttributes>): void {
    const state = event.filterState ?? {};
    const asText = (v: unknown): string | undefined => {
      if (typeof v === 'string' && v.trim()) return v.trim();
      if (Array.isArray(v) && v.length && typeof v[0] === 'string') return v[0];
      return undefined;
    };
    this.pageService.setColumnFilters({
      code: asText(state['code']),
      type: asText(state['type']),
      companyName: asText(state['company.name']),
    });
  }

  private openResultModal(row: LogMiddlewareServiceAttributes): void {
    this.dialog.open(ResponseLatencyModalComponent, {
      width: '400px',
      data: row,
      disableClose: true,
    });
  }

  private openDetailModal(row: LogMiddlewareServiceAttributes): void {
    // Nota: en la línea estándar este punto valida el permiso VIEW_LOG de
    // Latencia; esta rama no tiene el sistema de permisos.
    this.responsiveDialog.open(IntegrationErrorDetailModalComponent, {
      size: 'xl',
      data: row,
    });
  }
}
